import * as cookie from "cookie";
import React from "react";

import { BaseAPI } from "api/base_api";
import { FORUM_URL } from "config";
import {
  IntercomConversationType,
  MinervaResourceSubtypeType,
  PollChoiceType,
  PollGradingTypeType,
  PollResponseType,
  PollSessionType,
  PollTypeType,
  ResourceTypeType,
} from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { AppDispatch } from "store/index";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { decodeFlightAuthToken } from "utils/auth";
import { getAuthToken } from "utils/cookie";

const MAX_RETRIES = 3;

type SignedUrlOptionsType = {
  iframed?: boolean;
  noPresence?: boolean;
  readOnly?: boolean;
  darkMode?: boolean;
  noWhitespace?: boolean;
};

export class ForumAPI extends BaseAPI {
  private dispatch?: AppDispatch;

  private csrfRetryCount = 0;

  constructor(dispatch?: AppDispatch, userId?: string) {
    super(FORUM_URL, userId);
    this.dispatch = dispatch;
  }

  // eslint-disable-next-line class-methods-use-this
  log(...args: unknown[]) {
    ForumAPI.log(...args);
  }

  // eslint-disable-next-line class-methods-use-this
  warn(...args: unknown[]) {
    ForumAPI.warn(...args);
  }

  // eslint-disable-next-line class-methods-use-this
  error(...args: unknown[]) {
    ForumAPI.error(...args);
  }

  /**
   *  Interceptor-like function to handle automatic org switching in Forum.
   */
  protected async sendRequest(
    ...args: Parameters<BaseAPI["sendRequest"]>
  ): ReturnType<BaseAPI["sendRequest"]> {
    const response = await super.sendRequest(...args);

    // Handle force reload
    const forceReload = response.headers.get("X-Force-Reload");
    if (forceReload) {
      this.warn("Force reload detected — triggering session refresh.");

      // .reload() is much slower and continues JS execution.
      // eslint-disable-next-line no-self-assign
      window.location.href = window.location.href;

      // Return a never-resolving Promise to prevent further continuation while waiting for the reload.
      return new Promise(() => {});
    }

    // Handle CSRF failures
    if (!response.ok && response.status === 403) {
      try {
        const errorText = await response.text();
        if (errorText.includes("CSRF Failed")) {
          if (this.csrfRetryCount < 2) {
            this.warn(
              `CSRF failure detected — attempting token refresh (attempt ${
                this.csrfRetryCount + 1
              }/2)`,
            );

            // Try to refresh tokens
            const refreshResult = await this.refreshTokens();

            this.csrfRetryCount += 1;
            if (refreshResult) {
              // Retry the original request
              this.log("Retrying request after token refresh");
              return super.sendRequest(...args);
            }
            this.error("Token refresh failed — logging error");
          } else {
            this.error("CSRF failure: maximum retry attempts (2) exceeded");
          }
        }
      } catch (error) {
        this.error("Error handling CSRF failure:", error);
      }
    }

    // Reset CSRF retry count on successful requests
    const method = args[0];
    if (response.ok && method === "POST") {
      this.csrfRetryCount = 0;
    }

    return response;
  }

  getHeaders(method: "POST" | "GET", url: string, sendAsFormData: boolean) {
    const csrftoken = cookie.parse(document.cookie).csrftoken;
    if (!csrftoken) {
      this.warn(
        `[sendRequest] Missing CSRF token for User ${this.userId}, method=${method}, url=${url}`,
      );
    }

    const headers: { Accept: string; "X-CSRFToken": string | undefined; "Content-Type"?: string } =
      {
        Accept: "application/json; dashes=no",
        "X-CSRFToken": csrftoken,
      };

    // https://muffinman.io/blog/uploading-files-using-fetch-multipart-form-data/
    if (!sendAsFormData) {
      headers["Content-Type"] = "application/json; dashes=no";
    }
    return headers;
  }

  requestErrorCallback(
    url: string,
    method: "POST" | "GET",
    data: string | FormData,
    status: number | string,
  ): void {
    this.dispatch?.(
      createMonitoringEvent({
        type: MonitoringEventTypeType.FAILED_API_REQUEST,
        userId: this.userId,
        contextData: {
          url,
          method,
          data: JSON.stringify(data),
          status,
        },
      }),
    );
  }

  createBreakout(
    classId: string,
    title: string,
    grouping: string,
    duration: number | undefined,
    start: Date,
    end: Date | null,
    presentUserIds: string[],
  ) {
    return this.post(
      "api/v1/breakout/breakouts",
      JSON.stringify({
        klass_id: classId,
        title,
        grouping,
        duration_in_seconds: duration,
        first_session: {
          started_on: start,
          ended_on: end,
        },
        present_user_ids: presentUserIds.map((userId) => parseInt(userId, 10)),
        flight: true,
      }),
    );
  }

  createBreakoutSession(
    breakoutId: string,
    start: Date,
    end: Date | null,
    presentUserIds: string[],
  ) {
    return this.post(
      "api/v1/breakout/breakout-sessions",
      JSON.stringify({
        breakout_id: breakoutId,
        started_on: start,
        ended_on: end,
        present_user_ids: presentUserIds.map((userId) => parseInt(userId, 10)),
        flight: true,
      }),
    );
  }

  createPoll(
    question: string,
    title: string,
    type: PollTypeType,
    studentVisible: boolean,
    multipleResponse: boolean,
    pollChoices: PollChoiceType[],
    grading_type: PollGradingTypeType,
  ) {
    return this.post(
      "api/v1/poll/polls",
      JSON.stringify({
        question,
        title,
        type,
        student_visible: studentVisible,
        teacher_visible: true,
        grading_type,
        multiple_response: multipleResponse,
        poll_choices: pollChoices,
        num_choices: pollChoices.length,
        flight: true,
      }),
    );
  }

  createClassPoll(classId: string, pollId: string) {
    return this.post(
      "api/v1/class-polls",
      JSON.stringify({
        klass_id: classId,
        poll_id: pollId,
      }),
    );
  }

  createPollSession(pollId: string) {
    return this.post(
      "api/v1/poll/poll-sessions",
      JSON.stringify({
        poll_id: pollId,
        flight: true,
      }),
    );
  }

  createResource(resource: {
    klass_id: string;
    title: string;
    type: ResourceTypeType;
    file?: File;
    subtype?: MinervaResourceSubtypeType;
    url?: string;
    is_public: boolean;
    precreated: boolean;
  }) {
    let data;
    if (resource.file) {
      data = new FormData();
      data.append("klass_id", resource.klass_id);
      data.append("title", resource.title);
      data.append("type", resource.type);
      data.append("file", resource.file);
      data.append("precreated", resource.precreated.toString());
    } else {
      data = JSON.stringify(resource);
    }
    return this.post("api/v1/create_flight_resource", data, !!resource.file);
  }

  enrollInOpenClass(classId: string) {
    return this.post(`api/v1/classes/${classId}/enroll_in_open_flight_class`);
  }

  async getFlightWhiteboardsSignedUrl(resourceId: string, classId: string) {
    const data = {
      klass_id: classId,
    };
    const response = await this.post(
      `api/v1/sle/flight_whiteboard/${resourceId}/sign_url`,
      JSON.stringify(data),
      false,
      MAX_RETRIES,
    );
    const responseJson = await response.json();
    return responseJson.signed_url;
  }

  async getForumDocSignedUrl(
    resourceId: string,
    options: SignedUrlOptionsType = {
      iframed: false,
      noPresence: false,
      readOnly: false,
      darkMode: false,
      noWhitespace: false,
    },
  ) {
    const data = {
      iframed: options.iframed,
      no_presence: options.noPresence,
      read_only: options.readOnly,
      dark_mode: options.darkMode,
      no_whitespace: options.noWhitespace,
    };
    const response = await this.post(
      `api/v1/sle/minerva_doc/${resourceId}/sign_url`,
      JSON.stringify(data),
      false,
      MAX_RETRIES,
    );
    const responseJson = await response.json();
    return responseJson.signed_url;
  }

  async getForumWorkbookSignedUrl(
    resourceId: string,
    options: SignedUrlOptionsType = {
      iframed: false,
      noPresence: false,
      readOnly: false,
    },
  ) {
    const data = {
      iframed: options.iframed,
      no_presence: options.noPresence,
      read_only: options.readOnly,
    };
    const response = await this.post(
      `api/v1/sle/resources/${resourceId}/sign_url`,
      JSON.stringify(data),
      false,
      MAX_RETRIES,
    );
    const responseJson = await response.json();
    return responseJson.signed_url;
  }

  async getPreclassResourceRollupSignedUrl(classId: string) {
    const response = await this.get(
      `api/v1/pre_class_work/classes/${classId}/pre_class_resource_rollup_url?iframed=false&no_presence=true`,
      MAX_RETRIES,
    );
    const responseJson = await response.json();
    return responseJson.signed_url;
  }

  async getMuxVideoToken(resourceId: string) {
    const response = await this.get(`api/v1/resources/${resourceId}/mux_video_token`, MAX_RETRIES);
    const responseJson = await response.json();
    return responseJson.token;
  }

  /**
   * Make a request to Forum to get a signed url for this resource.
   *
   * The resource must refer to an S3 object, i.e., it must have a URL
   * of the form "https://s3.amazonaws.com/<bucket>/<key>".
   *
   * Returns a promise that resolves to the signed url.
   *
   * Options:
   *  - contentDisposition: this controls the Content-Disposition
   *    header of the response from S3 when the url is fetched,
   *    which determines how the browser will handle the payload.
   *    When "inline" is specified, the browser will try to open
   *    the resource in a tab. When "attachment" is specified,
   *    it will try to download it.
   */
  async getSignedUrl(resourceId: string, options: { contentDisposition: "attachment" | "inline" }) {
    const response = await this.get(
      `api/v1/resources/${resourceId}/signed_url?content-disposition=${options.contentDisposition}`,
      MAX_RETRIES,
    );
    const responseJson = await response.json();
    return responseJson.url;
  }

  async fetchIP() {
    const response = await this.get(`api/v1/get_ip`);
    return response.json();
  }

  async startRecording(classId: string | undefined) {
    if (classId) {
      const response = await this.post(
        "api/v1/recording-sessions",
        JSON.stringify({ klass_id: classId }),
      );
      return response.json();
    }
  }

  async markRecordingAsStarted(recordingSessionId: string | undefined) {
    if (recordingSessionId) {
      const response = await this.post(
        `api/v1/recording-sessions/${recordingSessionId}/mark_started`,
        JSON.stringify({ timestamp: Math.round(Date.now() / 1000) }),
      );
      if (!response.ok) {
        console.error("[Recording] Failed to mark recording as started", response);
      }
    }
  }

  async stopRecording(recordingSessionId: string | undefined) {
    if (recordingSessionId) {
      const response = await this.post(
        `api/v1/recording-sessions/${recordingSessionId}/stop_recording`,
      );
      return response.json();
    }
  }

  async changeModality(classId: string, type: string) {
    return this.post(`api/v1/classes/${classId}/change_modality`, JSON.stringify({ type }));
  }

  async softReset(classId: string) {
    return this.post(`api/v1/classes/${classId}/soft_reset_class_session`);
  }

  async hardReset(classId: string) {
    return this.post(`api/v1/classes/${classId}/hard_reset_class_session`);
  }

  async importPollSession(pollSession: PollSessionType, pollResponses: PollResponseType[]) {
    return this.post(
      `api/v1/flight/poll-sessions/${pollSession.id}/import`,
      JSON.stringify({
        poll_session: pollSession,
        poll_responses: pollResponses,
      }),
    );
  }

  async checkAuth() {
    return this.get(`api/v1/orgs/check-auth`);
  }

  async refreshTokens() {
    try {
      const response = await this.get("api/v1/flight/csrf/refresh", 0);
      if (response.ok) {
        // Update the CSRF token from the new JWT
        const newJwt = getAuthToken();
        if (newJwt) {
          decodeFlightAuthToken(newJwt);
        }
        this.log("Tokens refreshed successfully");
        return true;
      }
      this.error("Failed to refresh tokens:", response.status);
      return false;
    } catch (error) {
      this.error("Error refreshing tokens:", error);
      return false;
    }
  }

  async getIntercomConversations(classId: string) {
    const response = await this.get(`api/v1/intercom/classes/${classId}/intercom-conversations`);
    return response.json() as Promise<IntercomConversationType[]>;
  }

  async sendBugReport(
    classId: string,
    subject: string,
    shortReport: string,
    submissionUrl: string,
    logsUrl: string,
    userUploadedFilesURLs: string[],
    retry: number = 0,
  ) {
    return this.post(
      "api/v1/log-reporter",
      JSON.stringify({
        classId,
        subject,
        shortReport,
        submissionUrl,
        logsUrl,
        sendEmail: true,
        userUploadedFilesURLs,
      }),
      false,
      retry,
    );
  }

  async becomeTeacher(classId: string, userId: string) {
    return this.post(
      `api/v1/classes/${classId}/add_teacher`,
      JSON.stringify({
        user_id: userId,
        new_role_for_old_teacher: "assistant",
        flight: true,
      }),
    );
  }
}

export const ForumAPIContext = React.createContext(new ForumAPI());
